<script lang="ts">
	/**
	 * The tier comparison table (Design §14).
	 *
	 * It renders the server's per-price **`action` discriminator directly** — `buy` | `current` |
	 * `upgrade` | `downgrade` | `unavailable` — and never compares ranks itself. §9.3's rank rule
	 * lives in `CatalogService`; deriving it a second time here would give the storefront its own
	 * opinion, and the two would drift into a button that offers an upgrade the API refuses.
	 *
	 * `action` is absent for an anonymous caller, which is not "no action": it means the server
	 * has no subscription to compare against, so every column reads as a plain purchase and the
	 * Buy control routes through sign-in.
	 *
	 * **The tier card lives in the column header, not in the first two body rows.** Name, price and
	 * the control are one decision and belong in one block; split across `thead` and two `tbody`
	 * rows they scrolled apart from each other the moment the feature list grew, and the customer
	 * ended up reading a column of ticks with no idea whose column it was. The header is
	 * `sticky top-0` for the same reason, and the label column `sticky left-0`: a comparison table
	 * that loses either axis while you scroll it compares nothing.
	 *
	 * **Rows come from `comparisonFeatures`, and from nothing else.** There used to be a
	 * `featureList` of marketing bullets as well, and the two could not agree by construction: a
	 * bullet list can only say included-or-not, so a ladder that differs by *amount* — 10 projects
	 * against 100 against unlimited — was a tick in every column and compared nothing. Whichever
	 * field an admin edited, the other went stale, and this table rendered whichever it happened to
	 * prefer. One field, one statement of what a tier gives.
	 */

	import { Badge, Button } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import PriceDisplay from './PriceDisplay.svelte';
	import RoutedLink from './RoutedLink.svelte';
	import { formatFriendlyDate, mediaURL, rungColor } from '../Utilities';
	import type { Price, Product, StorefrontCurrency } from '../AppTypes';

	export let products: Product[] = [];
	export let currency: StorefrontCurrency | undefined = undefined;
	/** From `/api/configuration`. False renders Buy disabled rather than a button that 500s. */
	export let isOpen: boolean = false;
	/** Only used for the Buy label; the action itself comes from the server. */
	export let isSignedIn: boolean = false;

	const dispatcher = createEventDispatcher();

	/** The price a column is about: the default one, falling back to the first listed. */
	function headlinePrice(product: Product): Price | null {
		return product.prices.find(price => price.isDefault) ?? product.prices[0] ?? null;
	}

	$: columns = [...products].sort((left, right) => left.rank - right.rank);

	/**
	 * One row of the matrix: a label, and what each column gives against it.
	 *
	 * `values` is keyed by product uuid rather than by column index so a re-sort cannot silently
	 * shift every value one column to the left — the failure mode there is a table that is wrong
	 * in a way that still looks entirely plausible.
	 */
	interface MatrixRow {
		label: string;
		/** `null` in the map means "included, no amount to state" and renders as a tick. */
		values: Map<string, string | null>;
	}
	interface MatrixGroup {
		title: string | null;
		rows: MatrixRow[];
	}

	/**
	 * The matrix, as the union across every column in first-seen order.
	 *
	 * First-seen rather than sorted, in both dimensions: the order the rows are written in is the
	 * order the person who wrote them meant them to be read, and alphabetising a feature list
	 * scatters a deliberate narrative. A label only one tier declares becomes a row with a dash
	 * against the rest, never a vanished row — the whole point of a comparison is what the other
	 * columns do *not* have.
	 */
	$: groups = structuredGroups(columns);

	function structuredGroups(list: Product[]): MatrixGroup[] {
		const result: MatrixGroup[] = [];
		for (const product of list) {
			for (const feature of product.comparisonFeatures ?? []) {
				const title = feature.group === null || feature.group === '' ? null : feature.group;
				let group = result.find(candidate => candidate.title === title);
				if (group === undefined) {
					group = { title, rows: [] };
					result.push(group);
				}
				let row = group.rows.find(candidate => candidate.label === feature.label);
				if (row === undefined) {
					row = { label: feature.label, values: new Map() };
					group.rows.push(row);
				}
				row.values.set(product.uuid, feature.value === '' ? null : feature.value);
			}
		}
		return result;
	}

	/**
	 * Hide the rows every tier answers identically (§14).
	 *
	 * A comparison table's job is the difference, and a row that reads the same across every
	 * column is not one — it is a row the customer scans, resolves to "no information", and pays
	 * for with the vertical space that pushed the real difference below the fold. Off by default,
	 * because the shared rows are also the reassurance that the entry tier is not a stub.
	 *
	 * A row a tier simply omits **is** a difference: `has` is compared as well as the value, so a
	 * dash against one column keeps the row.
	 */
	export let differencesOnly: boolean = false;

	function isDifferentiator(row: MatrixRow): boolean {
		const first = columns[0];
		if (first === undefined) return true;
		const present = row.values.has(first.uuid);
		const value = row.values.get(first.uuid) ?? null;
		return columns.some(
			product => row.values.has(product.uuid) !== present || (row.values.get(product.uuid) ?? null) !== value
		);
	}

	$: visibleGroups = differencesOnly
		? groups
				.map(group => ({ ...group, rows: group.rows.filter(isDifferentiator) }))
				// A section whose every row was shared collapses entirely: an empty heading band
				// reads as a rendering fault rather than as "nothing differs here".
				.filter(group => group.rows.length > 0)
		: groups;

	/** How many rows the filter is holding back, so the control can say what it did. */
	$: sharedRowCount =
		groups.reduce((count, group) => count + group.rows.length, 0) -
		visibleGroups.reduce((count, group) => count + group.rows.length, 0);

	function labelFor(price: Price | null): string {
		if (price === null) return 'Not available';
		switch (price.action) {
			case 'current':
				return 'Current plan';
			case 'upgrade':
				return 'Upgrade (prorated now)';
			case 'downgrade':
				return 'Downgrade (starts at period end)';
			case 'unavailable':
				return price.blockedReason === 'pendingChange'
					? `Blocked until ${price.pendingChangeEffectiveAt ? formatFriendlyDate(price.pendingChangeEffectiveAt) : 'the pending change lands'}`
					: 'Not available in this currency';
			default:
				return isSignedIn ? 'Buy' : 'Sign in to buy';
		}
	}

	function isDisabled(price: Price | null): boolean {
		return price === null || price.action === 'current' || price.action === 'unavailable' || price.unavailable || !isOpen;
	}

	/**
	 * The promoted column's tint.
	 *
	 * A deliberate brand accent rather than a `bg-depth` level: depth says "how nested is this",
	 * and this says "read this one first". Using a depth level for it would make the recommended
	 * tier look like a container, and would fight every cell that legitimately nests inside it.
	 */
	function columnTint(product: Product): string {
		return isPromoted(product) ? 'bg-primary-50 dark:bg-primary-500/10' : '';
	}

	/**
	 * The header cell's own background — the tint when there is one, the container's level when
	 * there is not.
	 *
	 * One opaque background per cell, chosen here rather than two classes stacked on the element.
	 * `bg-depth-current` is a descendant rule and outranks a Tailwind `bg-*` utility on the same
	 * element, so a tint written beside it loses silently; and putting the tint on an inner block
	 * instead leaves the top of the cell untinted, because `align-bottom` sizes that block to its
	 * own content and a short column's tint then starts partway down the header.
	 */
	function columnSurface(product: Product): string {
		return isPromoted(product) ? columnTint(product) : 'bg-depth-current';
	}

	function isPromoted(product: Product): boolean {
		return typeof product.highlight === 'string' && product.highlight.length > 0;
	}
</script>

<div class="widget bg-depth overflow-x-auto">
	<!--
		**`table-fixed`, and a floor computed from the column count.**

		Auto layout sizes each column to its own content, so a tier whose name is longer, whose
		tagline wraps to two lines, or whose feature values are wider gets a wider column — and a
		comparison table whose columns are different widths is one that has put its thumb on the
		scale. The tiers are meant to be read against each other, so they get equal space by
		construction rather than by whatever the copy happened to be.

		Fixed layout ignores content, so the width floor has to move to the table: the label column
		is `w-52` (13rem) and each tier gets at least as much again, past which the wrapper scrolls.
		A static `min-w-[44rem]` gave three tiers about 10rem each — under the floor, and worse the
		more tiers a line has. Inline, because a Tailwind class built at runtime is invisible to the
		JIT scanner and would silently never ship.
	-->
	<table
		class="w-full text-left border-collapse table-fixed"
		style={`min-width: ${13 * (columns.length + 1)}rem`}
	>
		<thead>
			<tr>
				<!--
					The corner, deliberately empty. A "Compare" label here says nothing a column of
					row headings does not already say, and `align-bottom` parked it against the first
					data row where it read as that row's label. `sticky` on both axes so it holds the
					corner while either direction scrolls.
				-->
				<th class="w-52 sticky left-0 top-0 z-30 bg-depth-current px-4 py-3"><span class="sr-only">Feature</span></th>
				{#each columns as product, index (product.uuid)}
					{@const price = headlinePrice(product)}
					<th class="relative align-bottom sticky top-0 z-20 p-0 {columnSurface(product)}">
						<!--
							The ascent, drawn per column rather than as one rail: `rank` is what makes
							"upgrade" and "downgrade" definable (§9.3), and a table that renders an
							ordered ladder with no visible ordering throws that away. Same ramp as the
							`h1` underline, and the same device the catalogue's `TierLadder` uses, so
							the two screens read as one system. Inline style, because a Tailwind class
							built at runtime is invisible to the JIT scanner.
						-->
						<!--
							**Absolutely positioned, not the first child in the flow.** The cell is
							`align-bottom` so tier cards of different heights sit on one baseline —
							which also pushes a shorter column's *entire* content down, the bar
							included. The bars then stepped down the row in the order of how much copy
							each tier happened to have, which looks like sloppy alignment and hides
							the ordering the bar exists to show. Anchored to the cell instead, every
							bar sits on one line whatever its column holds.
						-->
						<div
							class="absolute inset-x-0 top-0 h-1"
							style={`background-color: ${rungColor(index, columns.length)}`}
							aria-hidden="true"
						/>
						<div class="px-4 py-3 flex flex-col gap-2">
							<div class="flex flex-wrap items-center gap-2 min-h-[1.5rem]">
								{#if isPromoted(product)}
									<!-- The line's own recommendation, in its own words. -->
									<Badge color="blue" class="uppercase tracking-wide text-[10px]">
										<i class="fa-solid fa-star mr-1 text-[9px]" />{product.highlight}
									</Badge>
								{/if}
								{#if price?.action === 'current'}
									<!-- Text, never colour alone: the held tier has to be readable as words. -->
									<Badge color="green">Current plan</Badge>
								{/if}
							</div>

							<!--
								The tier's own logo, on the one screen that puts the tiers side by side.
								A column of names and prices is the hardest place to tell a line's
								products apart, and the icon is the fastest thing that does it.

								Rendered only when there is one, and with no reserved box when there is
								not: an empty 32px square beside every name reads as a failed image
								rather than as a tier with no logo, and a *row* of them reads as a
								broken page.
							-->
							<div class="flex items-center gap-2 min-w-0">
								{#if product.imageURL}
									<img
										src={mediaURL(product.imageURL)}
										alt=""
										class="w-8 h-8 rounded-lg object-contain shrink-0"
										loading="lazy"
									/>
								{/if}
								<RoutedLink
									to={`/Product/${product.slug}`}
									class="text-xl font-bold text-gray-900 dark:text-white hover:text-primary-500 truncate"
								>
									{product.name}
								</RoutedLink>
							</div>
							{#if product.tagline}
								<span class="text-xs text-default font-normal leading-snug">{product.tagline}</span>
							{/if}

							<PriceDisplay
								{price}
								{currency}
								currencyCode={product.currency}
								showTaxNote={false}
								class="mt-1 tabular-nums"
							/>

							<Button
								size="sm"
								class="w-full mt-1"
								color={price?.action === 'current' ? 'alternative' : 'primary'}
								disabled={isDisabled(price)}
								on:click={() => dispatcher('select', { product, price })}
							>
								{labelFor(price)}
							</Button>
							{#if !isOpen}
								<p class="text-xs text-default">Not open yet.</p>
							{/if}
						</div>
					</th>
				{/each}
			</tr>
		</thead>
		<tbody>
			{#if groups.length === 0}
				<!--
					The line has not described its tiers yet. A silently empty table under a row of
					prices reads as a page that failed to load, rather than as a catalogue nobody has
					finished writing.
				-->
				<tr class="border-t border-gray-200/70 dark:border-zinc-700/70">
					<td colspan={columns.length + 1} class="px-4 py-6 text-sm text-default">
						These tiers have not been described row by row yet. The prices above are the whole
						comparison for now.
					</td>
				</tr>
			{:else if visibleGroups.length === 0}
				<!--
					Every row reads the same across every tier. That is a real answer to "what is
					different", and a table that simply emptied itself would read as a fault.
				-->
				<tr class="border-t border-gray-200/70 dark:border-zinc-700/70">
					<td colspan={columns.length + 1} class="px-4 py-6 text-sm text-default">
						These tiers list the same things. What separates them is the price above, and
						anything the line has not written down yet.
					</td>
				</tr>
			{/if}

			{#each visibleGroups as group, groupIndex (group.title ?? `__ungrouped-${groupIndex}`)}
				{#if group.title !== null}
					<tr>
						<!--
							The heading cell spans only the label column, and the tier columns get
							their own cells behind it.

							It was one `colspan` band across the whole row, which read well on its own
							and cut the promoted column in half: the tint ran down the header and the
							data rows and stopped dead at every section heading, so the one column the
							line is recommending was a stack of disconnected blocks rather than a
							column. A tinted cell per column keeps it unbroken top to bottom, and the
							heading still reads as a heading — it is the only thing on the row, it
							carries `card-label`, and it has the space above it.
						-->
						<th scope="colgroup" class="sticky left-0 z-10 bg-depth-current text-left px-4 pt-5 pb-2">
							<span class="card-label">{group.title}</span>
						</th>
						{#each columns as product (product.uuid)}
							<td class="px-4 pt-5 pb-2 {columnTint(product)}" aria-hidden="true" />
						{/each}
					</tr>
				{/if}

				{#each group.rows as row (`${group.title ?? ''}:${row.label}`)}
					<tr class="border-t border-gray-200/70 dark:border-zinc-700/70">
						<th
							scope="row"
							class="sticky left-0 z-10 bg-depth-current text-sm font-medium text-default text-left align-top px-4 py-2.5 break-words"
						>
							{row.label}
						</th>
						{#each columns as product (product.uuid)}
							<!-- `break-words`: a fixed column cannot grow for its content, so an unbroken
							     value has to wrap rather than spill across the tier beside it. -->
							<td class="align-top text-sm px-4 py-2.5 break-words {columnTint(product)}">
								{#if !row.values.has(product.uuid)}
									<span class="text-default" aria-label="Not included">—</span>
								{:else if row.values.get(product.uuid) === null}
									<i class="fa-solid fa-check text-green-600 dark:text-green-400" aria-label="Included" />
								{:else}
									<!-- The amount is the comparison. A tick beside it would say less. -->
									<span class="font-medium">{row.values.get(product.uuid)}</span>
								{/if}
							</td>
						{/each}
					</tr>
				{/each}
			{/each}
		</tbody>
	</table>
</div>
